import { Icon } from './Icon';
import { useMyNotifications } from '../lib/notifications';
import { ALERTS_WINDOW } from '../lib/familyAlerts';
import { isStaffRole, useUserDoc, type UserDoc } from '../lib/users';
import { useStaffWaitingOnYouCount } from '../lib/staffWaitingOnYou';
import { staffBellLabel, waitingCheckFailed, waitingCountLabel } from '../lib/staffWaitingOnYouCore';

function Bell({
  count,
  href,
  label,
  incomplete = false,
}: {
  count: number;
  href: string;
  label: string;
  /** A check failed, so the count is only what survived: the dot shows "?"
   *  or "3+" rather than disappearing at 0. A full list is not a failure. */
  incomplete?: boolean;
}) {
  return (
    <a
      href={href}
      aria-label={label}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        // The 44px tap floor (CLAUDE.md). It was 40x40, found by the
        // 2026-10-04 role crawl of the staff home.
        width: 'var(--tap-target)',
        height: 'var(--tap-target)',
        borderRadius: 'var(--radius-pill)',
        border: '1px solid var(--line)',
        background: 'var(--paper)',
        color: 'var(--ink)',
        boxShadow: 'var(--shadow-sm)',
        textDecoration: 'none',
      }}
    >
      <Icon name="bell" size={18} />
      {(count > 0 || incomplete) && (
        <span
          style={{
            position: 'absolute',
            top: -3,
            right: -3,
            minWidth: 18,
            height: 18,
            padding: '0 5px',
            borderRadius: 'var(--radius-pill)',
            background: 'var(--lime)',
            color: 'var(--ink)',
            fontSize: 11,
            fontWeight: 800,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 'var(--shadow-sm)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {waitingCountLabel(count, incomplete, 9)}
        </span>
      )}
    </a>
  );
}

function StaffNotifBell({ userDoc }: { userDoc: UserDoc }) {
  const waiting = useStaffWaitingOnYouCount(userDoc);
  return (
    <Bell
      count={waiting.count}
      href="/messages"
      label={staffBellLabel(waiting.count, waiting)}
      incomplete={waitingCheckFailed(waiting)}
    />
  );
}

function FamilyNotifBell() {
  // The same window the notifications page this bell opens lists, so the
  // number on the bell is never counted from cards the page cannot show.
  const { unread } = useMyNotifications(ALERTS_WINDOW);
  return <Bell count={unread} href="/notifications" label="Notifications" />;
}

export function NotifBell() {
  const { userDoc } = useUserDoc();
  if (userDoc?.status !== 'active') return null;
  return isStaffRole(userDoc.role)
    ? <StaffNotifBell userDoc={userDoc} />
    : <FamilyNotifBell />;
}
